import logo from "../../../images/logo.png";
import { useState, useEffect } from "react";

// Utils
import { dateToTime } from "../../../utils/timeFormatter";

// Components
import {
	markSessionAsAbandoned,
	endSessionManual,
	checkBoothOccupancy,
} from "../../../services/api";
import TimeSlotSelector from "../../../components/TimeSlotSelector";
import ActiveSessionLoadingState from "../../../components/ActiveSessionLoadingState";
import ElapsedTimerDisplay from "../../../components/ElapsedTimerDisplay";
import TimeRemainingDisplay from "../../../components/TimeRemainingDisplay";

export default function ActiveSession({ sessionData }) {
	const [pageLoading] = useState(false);
	const [showAvailableTime, setShowAvailableTime] = useState(false);
	const [endingSession, setEndingSession] = useState(false);
	const [occupancyData, setOccupancyData] = useState(sessionData.occupancyData);
	const [lastPollTime, setLastPollTime] = useState(null);
	const [isManualRefreshing, setIsManualRefreshing] = useState(false);

	// Poll occupancy status using REST API
	useEffect(() => {
		let pollInterval;

		const pollOccupancy = async () => {
			try {
				const sessionId = sessionData.sessionId || sessionData._id;
				console.log(`📡 [OCCUPANCY] Polling occupancy for session ${sessionId}`);

				const result = await checkBoothOccupancy(sessionId);

				// Update occupancy data if available
				if (result.occupancyData) {
					setOccupancyData(result.occupancyData);
					setLastPollTime(new Date());
					console.log(`📊 [OCCUPANCY] Status: ${result.occupancyData.isOccupied ? 'OCCUPIED' : 'VACANT'}`);
				}

				// If session status changed (e.g., to pending_payment), parent will handle it
				// Parent Reservation.js component listens to Pusher session-update events

			} catch (error) {
				console.error('❌ [OCCUPANCY] Polling error:', error);
			}
		};

		// Only poll if we have a Density sensor configured
		if (sessionData.booth?.densitySensorId) {
			// Poll immediately on mount
			pollOccupancy();

			// Then poll every 10 seconds
			pollInterval = setInterval(pollOccupancy, 10000);
			console.log('✅ [OCCUPANCY] Started polling (10s interval)');
		}

		// Cleanup
		return () => {
			if (pollInterval) {
				clearInterval(pollInterval);
				console.log('🔌 [OCCUPANCY] Stopped polling');
			}
		};
	}, [
		sessionData.sessionId,
		sessionData._id,
		sessionData.booth?.densitySensorId,
	]);

	// Manual refresh function for debug panel
	const handleManualRefresh = async () => {
		try {
			setIsManualRefreshing(true);
			const sessionId = sessionData.sessionId || sessionData._id;
			console.log(`🔄 [OCCUPANCY] Manual refresh triggered for session ${sessionId}`);

			const result = await checkBoothOccupancy(sessionId);

			if (result.occupancyData) {
				setOccupancyData(result.occupancyData);
				setLastPollTime(new Date());
				console.log(`📊 [OCCUPANCY] Manual refresh - Status: ${result.occupancyData.isOccupied ? 'OCCUPIED' : 'VACANT'}`);
			}
		} catch (error) {
			console.error('❌ [OCCUPANCY] Manual refresh error:', error);
		} finally {
			setIsManualRefreshing(false);
		}
	};

	// Handle session abandonment
	const handleAbandonSession = async () => {
		try {
			const res = await markSessionAsAbandoned(sessionData._id);
			console.log("Session abandoned", res);
		} catch (error) {
			console.error("Error abandoning session:", error);
		}
	};

	const handleExtendSession = (selectedSlotId) => {
		// Implement your session extension logic here
		console.log("Extending session with slot:", selectedSlotId);
		// You can make an API call here to extend the session
	};

	const handleEndSession = async () => {
		try {
			setEndingSession(true);
			console.log("🔚 [FRONTEND] Manually ending session:", sessionData._id);

			const result = await endSessionManual(sessionData._id);
			console.log("🔚 [FRONTEND] Session ended successfully:", result);

			// The Pusher event will handle the UI update to finished state
		} catch (error) {
			console.error("❌ End session failed:", error);
			// Handle error display here if needed
		} finally {
			setEndingSession(false);
		}
	};

	// Show loading state while processing or if no session data
	if (pageLoading || !sessionData) {
		return (
			<ActiveSessionLoadingState message="Loading your active session..." />
		);
	}

	return (
		<div className="bg-white min-h-screen flex flex-col items-center px-4 py-6">
			{/* Logo with Sponsor */}
			<div className="flex flex-col items-center mb-6">
				<img
					src={logo}
					className="w-40 h-auto object-contain"
					alt="Pebble Spaces Logo"
				/>
			</div>

			{/* Unlocked Status Card */}
			<div className="p-6 mb-6 w-full max-w-sm text-center">
				<h2 className="text-2xl font-bold text-black mb-2">Your Session</h2>
				<p className="text-black text-base">
					Your Pebble is unlocked! Step into your space and track your session
					here.
				</p>
			</div>

			{/* Session Details */}
			<div className="w-full max-w-sm mb-4">
				{sessionData.type === "reservation" && (
					<div className="border-t border-gray-300 pt-4 pb-2">
						<p className="text-lg font-medium text-black text-center">
							Reserved Until{" "}
							{sessionData ? dateToTime(sessionData.endTime) : "9:30 AM"}
						</p>
					</div>
				)}
				<div
					className={`border-b border-gray-300 pb-4 ${
						sessionData.type === "paid-byMinute" ? "border-t pt-4" : ""
					}`}
				>
					<p className="text-base text-gray-600 text-center">
						Pebble {sessionData?.booth?.boothId || "1"} |{" "}
						{sessionData?.booth?.location?.name ||
							"Marriott Hotel Times Square"}
					</p>
				</div>
			</div>

			{/* Time Display */}
			{sessionData.type === "reservation" && (
				<TimeRemainingDisplay
					endTime={sessionData.endTime}
					onAbandon={handleAbandonSession}
				/>
			)}

			{sessionData.type === "paid-byMinute" && (
				<>
					<ElapsedTimerDisplay
						startTime={sessionData.startTime}
						isOverdue={false}
					/>
					{/* <CostDisplay
						startTime={sessionData.startTime}
						costPerMinute={sessionData.booth?.costPerMinute || 0}
						isOverdue={false}
					/> */}
				</>
			)}

			{/* Occupancy Debug Panel (Development Only) */}
			{process.env.NODE_ENV === "development" && (
				<div className="w-full max-w-sm mb-6 bg-gray-50 border border-gray-200 rounded-lg p-4">
					<div className="flex items-center justify-between mb-3">
						<h3 className="text-lg font-bold text-gray-800 flex items-center">
							<span className="mr-2">📡</span>
							Density Sensor Debug
						</h3>
						<button
							onClick={handleManualRefresh}
							disabled={isManualRefreshing}
							className="px-3 py-1 bg-blue-600 text-white text-xs font-medium rounded hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
						>
							{isManualRefreshing ? "⟳" : "🔄"} Refresh
						</button>
					</div>

					<div className="space-y-3">
						{occupancyData ? (
							<>
								{/* Current Occupancy Status */}
								<div className="bg-white rounded-md p-3 border">
									<div className="font-medium text-gray-700 mb-2">
										Current Status
									</div>
									<div className="space-y-2">
										<div className="flex items-center justify-between">
											<span className="text-sm text-gray-600">Occupied:</span>
											<span
												className={`px-2 py-1 rounded-full text-xs font-medium ${
													occupancyData.isOccupied
														? "bg-green-100 text-green-800"
														: "bg-red-100 text-red-800"
												}`}
											>
												{occupancyData.isOccupied ? "✅ YES" : "❌ NO"}
											</span>
										</div>
										<div className="flex items-center justify-between">
											<span className="text-sm text-gray-600">Raw Count:</span>
											<span className="text-sm font-mono font-medium text-gray-800">
												{occupancyData.count ?? "N/A"}
											</span>
										</div>
										<div className="flex items-center justify-between">
											<span className="text-sm text-gray-600">Space ID:</span>
											<span className="text-xs font-mono text-gray-600">
												{occupancyData.spaceId || sessionData.booth?.densitySpaceId || "N/A"}
											</span>
										</div>
										{lastPollTime && (
											<div className="flex items-center justify-between">
												<span className="text-sm text-gray-600">Last Poll:</span>
												<span className="text-xs text-gray-600">
													{lastPollTime.toLocaleTimeString()}
												</span>
											</div>
										)}
									</div>
								</div>

								{/* Polling Info */}
								<div className="bg-blue-50 border border-blue-200 rounded-md p-2 text-center">
									<div className="text-xs text-blue-700">
										Auto-polling every 10 seconds
									</div>
								</div>
							</>
						) : (
							/* No Sensor Configured */
							<div className="bg-yellow-50 border border-yellow-200 rounded-md p-4 text-center">
								<div className="text-yellow-800 font-medium mb-2">
									⚠️ No Density Sensor Configured
								</div>
								<div className="text-sm text-yellow-700">
									This booth (ID: {sessionData.booth?.boothId}) doesn't have a
									Density sensor configured.
								</div>
								<div className="text-xs text-yellow-600 mt-2">
									Configure a Density sensor in the admin panel to enable
									occupancy monitoring.
								</div>
							</div>
						)}
					</div>
				</div>
			)}

			{/* Check-Out Policy */}
			<div className="w-full max-w-sm mb-8">
				<h3 className="text-2xl font-bold text-black mb-4 text-center">
					Check-Out Policy
				</h3>
				<ul className="space-y-3 text-sm text-gray-700">
					<li className="flex items-start">
						<span className="inline-block w-2 h-2 bg-black rounded-full mt-2 mr-3 flex-shrink-0"></span>
						<span>Exit and close the door to end your session.</span>
					</li>
					{sessionData.type === "paid-byMinute" && (
						<li className="flex items-start">
							<span className="inline-block w-2 h-2 bg-black rounded-full mt-2 mr-3 flex-shrink-0"></span>
							<span>
								Your receipt will be texted to you after ending your session
							</span>
						</li>
					)}

					{sessionData.type === "reservation" && (
						<li className="flex items-start">
							<span className="inline-block w-2 h-2 bg-black rounded-full mt-2 mr-3 flex-shrink-0"></span>
							<span>
								<strong>
									Overstaying by more than 2 minutes triggers a $25 penalty.
								</strong>{" "}
								This fee is not kept by Pebble and is donated to a nonprofit to
								help ensure fair use for the next user
							</span>
						</li>
					)}
					<li className="flex items-start">
						<span className="inline-block w-2 h-2 bg-black rounded-full mt-2 mr-3 flex-shrink-0"></span>
						<span>
							It may take up to 5 minutes to end your session and send your
							receipt after exiting. You will only be billed for your time
							inside the Pebble.
						</span>
					</li>
				</ul>
			</div>

			{/* Manual End Session Button for Walkup Sessions */}
			{sessionData.mode === "walkup" &&
				process.env.NODE_ENV === "development" && (
					<div className="w-full max-w-sm mb-6">
						<button
							className="bg-red-600 text-white w-full py-4 rounded-lg text-base font-semibold transition-colors duration-200 hover:bg-red-700"
							onClick={handleEndSession}
							disabled={endingSession}
						>
							{endingSession ? (
								<>
									<div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin inline-block mr-2"></div>
									Ending Session...
								</>
							) : (
								"🔚 End Session (Simulate Exit)"
							)}
						</button>

						<p className="text-xs text-gray-500 text-center mt-2">
							⚠️ Development: Simulates Waffle Radar detecting you leaving the
							booth
						</p>
					</div>
				)}

			{/* Help Button */}
			<button
				className="w-full max-w-sm bg-white border border-gray-300 rounded-lg py-4 px-6"
				onClick={() => {
					window.open("https://pebblespaces.com/contact", "_blank");
				}}
			>
				<span className="text-base font-semibold text-black">
					Need Help? Report a Problem
				</span>
			</button>

			{/* Time Slot Selector Modal */}
			{showAvailableTime && (
				<TimeSlotSelector
					onClose={() => setShowAvailableTime(false)}
					onSelect={handleExtendSession}
				/>
			)}
		</div>
	);
}
